import React, { Component } from "react";
import { Stack, TextField, Container as UIContainer } from "@mui/material";
import { observable } from "mobx";
import { observer } from "mobx-react";
import styled from "styled-components";

import { defined } from "@formant/core/common/defined";
import { AddableModuleType } from "@formant/core/model/AddableModuleType";
import { ICustomModuleParameters } from "@formant/core/model/ICustomModuleParameters";
import { IDeviceConfiguration } from "@formant/core/model/IDeviceConfiguration";
import { IGeoJsonIcon } from "@formant/core/model/IGeoJsonIcon";
import { ILayoutModuleConfiguration } from "@formant/core/model/ILayoutModuleConfiguration";
import { ModuleMode } from "@formant/core/model/ModuleMode";
import { Button } from "@formant/ui-kit";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";

import { track } from "../../analytics";
import { deviceClient } from "../../clients";
import {
    appStore,
    authStore,
    customModuleStore,
    deviceStore,
    metadataStore,
    observeStore,
} from "../../stores";
import { customModuleList } from "../../utils/builtInCustomModuleList";
import { sortCaseInsensitive } from "../../utils/sortLowercase";
import { IconButton } from "../common/IconButton";
import { MultiSelect } from "../common/MultiSelect";
import { Popup } from "../common/Popup";
import { Scrollable } from "../common/Scrollable";
import { Switch } from "../common/Switch";
import { TextareaField } from "../common/TextareaField";
import { Heading, Scroll, Title } from "../JsonSchemaForm";
import { Label } from "../JsonSchemaForm/Label";
import { ListButton } from "../JsonSchemaForm/ListButton";
import { JsonSchema } from "../JsonSchemaForm/types";
import { ButtonBar } from "../ViewDevice/DeviceHeaderButtons/RosTopicModal/RosTopicModal.styles";
import { IModuleConfigurationProps } from "./IModuleConfigurationProps";
import { ModuleConfigurationSchemaForm } from "./ModuleConfigurationSchemaForm";

@observer
export class ModuleConfiguration extends Component<IModuleConfigurationProps> {
    @observable
    moduleMode: ModuleMode = ModuleMode.Observe;

    @observable
    useCustomView: boolean = false;

    @observable
    visible: boolean = true;

    @observable
    isDefinedCustomView: boolean = true;

    @observable
    debug: boolean | undefined;

    @observable
    showOnlyCurrentValues: boolean | undefined;

    @observable
    customName: string | undefined;

    @observable
    customVisualizationUrl: string | undefined;

    @observable
    streams: string[] = [];

    @observable
    geoJsonStreams: string[] = [];

    @observable
    knownStreams: string[] = [];

    @observable
    knownGeoJsonStream: string[] | undefined;

    @observable
    geoJsonIcons: IGeoJsonIcon[] = [];

    @observable
    iconId: string = "";

    @observable
    iconUrl: string = "";

    @observable
    moduleDefinitionId: string | undefined;

    @observable
    moduleConfigurationJSON: string | undefined;

    @observable
    moduleConfigurationId: string | undefined;

    @observable
    loadedModuleConfigurationSchema: boolean = false;

    @observable
    moduleConfigurationSchema: JsonSchema | undefined;

    @observable
    loading: boolean = true;

    @observable
    isValidUrl: boolean = true;

    getConfigurationFromStore = (): ILayoutModuleConfiguration | undefined => {
        const { streamName } = this.props;
        if (observeStore.configuringModule) {
            this.moduleMode = ModuleMode.Observe;
            return defined(observeStore.configuringModule)[1][streamName]
                ?.configuration;
        }
        if (observeStore.configuringFlyout) {
            track("Navigator: Customize: Configure Module", {
                stream: observeStore?.configuringFlyout?.[1].streamName,
            });

            this.moduleMode = ModuleMode.Teleop;
            return defined(observeStore.configuringFlyout)[0][streamName]
                ?.configuration;
        }
    };

    async componentDidMount() {
        const { moduleType } = this.props;
        if (moduleType === "analytics") {
            this.onCancel();
            return;
        }

        const configuration = this.getConfigurationFromStore();
        this.customVisualizationUrl = configuration?.customVisualizationUrl;
        this.customName = configuration?.customName;
        this.debug = configuration?.debug;
        this.showOnlyCurrentValues = configuration?.showOnlyCurrentValues;
        this.streams = configuration?.streams || [];
        if (configuration && configuration.moduleType === "location") {
            this.geoJsonStreams =
                (
                    configuration as
                        | ILayoutModuleConfiguration<"location">
                        | undefined
                )?.parameters?.geoJsonLayers?.map(_ => _.streamId) || [];
            this.geoJsonIcons =
                (
                    configuration as
                        | ILayoutModuleConfiguration<"location">
                        | undefined
                )?.parameters?.geoJsonIcons || [];
        }
        if (this.customVisualizationUrl || moduleType === "custom") {
            this.useCustomView = true;
        }
        let telemetryStreamNames: string[] = [];
        if (this.props.filter.names && this.props.filter.names.length > 0) {
            telemetryStreamNames = await metadataStore.fetchStreamNames({
                names: this.props.filter.names,
                start: observeStore.metadataStart.toISOString(),
                end: observeStore.metadataEnd.toISOString(),
            });
        }
        let telemetryStreamTypes: string[] = [];
        if (this.props.filter.types && this.props.filter.types.length > 0) {
            telemetryStreamTypes = await metadataStore.fetchStreamNames({
                types: this.props.filter.types,
                start: observeStore.metadataStart.toISOString(),
                end: observeStore.metadataEnd.toISOString(),
            });
        }
        let telemetryStreamTags: string[] = [];
        if (this.props.filter.tags) {
            telemetryStreamTags = await metadataStore.fetchStreamNames({
                tags: this.props.filter.tags,
                start: observeStore.metadataStart.toISOString(),
                end: observeStore.metadataEnd.toISOString(),
            });
        }

        const streams = [
            ...new Set([
                ...telemetryStreamNames,
                ...telemetryStreamTypes,
                ...telemetryStreamTags,
            ]),
        ];
        this.knownStreams = streams.sort(sortCaseInsensitive);

        const token = await authStore.token();
        const deviceConfigurations = await Promise.all(
            (
                await deviceStore.fetchList({
                    tags: this.props.filter.tags,
                })
            )
                .map(_ => ({
                    id: _.id,
                    version: _.desiredConfigurationVersion,
                    enabled: _.enabled,
                }))
                .reduce<Promise<IDeviceConfiguration>[]>((prev, _) => {
                    if (_.id && _.version && _.enabled) {
                        prev.push(
                            deviceClient.getConfiguration(
                                token,
                                _.id,
                                _.version,
                            ),
                        );
                    }
                    return prev;
                }, []),
        );

        const geoJsonStreamNames = deviceConfigurations.reduce<string[]>(
            (prev, _) => {
                _.document.telemetry?.streams?.forEach(s => {
                    if (s.validation?.schemaId === "geojson") {
                        prev.push(s.name);
                    }
                });
                return prev;
            },
            [],
        );

        this.knownGeoJsonStream = geoJsonStreamNames.filter(
            (value, index, self) => self.indexOf(value) === index,
        );

        if (moduleType === "custom") {
            await this.handleDefaultModulesConfiguation();
            if (configuration?.parameters) {
                const configId = (
                    configuration.parameters as ICustomModuleParameters
                ).customModuleConfigurationId;
                this.moduleConfigurationId = configId;
                const moduleDefinitionId = (
                    configuration.parameters as ICustomModuleParameters
                ).customModuleDefinitionId;
                this.moduleDefinitionId = moduleDefinitionId;
                if (configId) {
                    const fileContentsUrls =
                        await customModuleStore.getConfiguration(configId);
                    const fileText = fileContentsUrls?.configuration;
                    if (fileText) {
                        this.moduleConfigurationJSON = fileText;
                    }
                }

                if (moduleDefinitionId) {
                    const module =
                        await customModuleStore.fetch(moduleDefinitionId);
                    if (module) {
                        const schemaUrl = module.configurationSchemaUrl;
                        if (schemaUrl) {
                            await this.fetchModuleConfiguration(schemaUrl);
                            this.loadedModuleConfigurationSchema = true;
                        } else {
                            this.loadedModuleConfigurationSchema = true;
                        }
                    }
                } else {
                    this.isDefinedCustomView = false;
                    this.loadedModuleConfigurationSchema = true;
                }
            } else {
                this.isDefinedCustomView = false;
                this.loadedModuleConfigurationSchema = true;
            }
        }
        if (configuration?.visible != null) {
            this.visible = configuration.visible;
        }
        this.loading = false;
    }

    public prerender() {
        const {
            onSave,
            onCancel,
            onToggleCustomView,
            useCustomView,
            customVisualizationUrl,
            onChangeCustomName,
            onChangeCustomView,
            customName,
            debug,
            showOnlyCurrentValues,
            onToggleShowOnlyCurrentValues,
            loading,
            moduleMode,
            visible,
            onToggleVisibility,
            onInvalidUrl,
        } = this;

        const { streamName, moduleType } = this.props;

        const ScrollerComponent =
            moduleMode === ModuleMode.Observe ? Scroll : Scrollable;
        return (
            <>
                <Heading>
                    <Title type="h3">{`${
                        customName || "Module"
                    } Settings`}</Title>
                </Heading>
                <ScrollerComponent>
                    <LoadingIndicator visible={loading} />
                    <Content>
                        {!loading && this.moduleDefinitionId === undefined && (
                            <>
                                <Label
                                    value="Display module"
                                    description="Hide the module users do not need to see raw data from this stream but still want any events configured from the data to show on this view."
                                />
                                <ListButton>
                                    <Switch
                                        state={visible}
                                        onChange={onToggleVisibility}
                                        label={
                                            visible
                                                ? "Display On"
                                                : "Display Off"
                                        }
                                    />
                                </ListButton>
                            </>
                        )}
                        {moduleType !== "custom" && (
                            <>
                                <Label
                                    value="Use custom view"
                                    description="Custom visualizations let you view your module's data how you want."
                                />
                                <ListButton>
                                    <Switch
                                        state={useCustomView}
                                        onChange={onToggleCustomView}
                                        label={useCustomView ? "True" : "False"}
                                    />
                                </ListButton>
                            </>
                        )}

                        {(moduleType === "numeric set" ||
                            moduleType === "battery" ||
                            moduleType === "numeric") && (
                            <>
                                <Label
                                    value="Show only current values"
                                    description="For performance reasons you might want to limit this module to showing only current values."
                                />
                                <ListButton>
                                    <Switch
                                        state={showOnlyCurrentValues || false}
                                        onChange={onToggleShowOnlyCurrentValues}
                                        label={
                                            showOnlyCurrentValues
                                                ? "True"
                                                : "False"
                                        }
                                    />
                                </ListButton>
                            </>
                        )}
                        {moduleType === "location" && (
                            <>
                                <MultiSelect
                                    value={this.geoJsonStreams}
                                    title={
                                        this.knownGeoJsonStream
                                            ? "GeoJSON streams"
                                            : "Loading GeoJSON streams"
                                    }
                                    options={(
                                        this.knownGeoJsonStream || []
                                    ).map(_ => ({
                                        value: _,
                                    }))}
                                    onChange={this.onChangeGeoJsonStreams}
                                />
                                <Stack
                                    direction="column"
                                    spacing={2}
                                    alignItems="center"
                                >
                                    <Label value="Icon Id" />
                                    <TextField
                                        variant="filled"
                                        label="icon id"
                                        fullWidth={false}
                                        value={this.iconId}
                                        onChange={this.onChangeIconId}
                                    />
                                    <Label value="Icon Url" />
                                    <TextField
                                        variant="filled"
                                        label="icon url"
                                        fullWidth={false}
                                        value={this.iconUrl}
                                        onChange={this.onChangeIconUrl}
                                    />
                                    <Button
                                        variant="default"
                                        onClick={this.onAddIcon}
                                        size="lg"
                                        disabled={
                                            this.iconId.trim().length === 0 ||
                                            this.iconUrl.trim().length === 0
                                        }
                                    >
                                        Add Icon
                                    </Button>
                                </Stack>
                                <UIContainer
                                    sx={{
                                        maxHeight: "5rem",
                                        overflowY: "scroll",
                                    }}
                                >
                                    {this.geoJsonIcons.map((_, i) => (
                                        <div key={i}>
                                            {_.iconId} - {_.url}{" "}
                                            <IconButton
                                                icon="delete"
                                                onClick={() =>
                                                    this.onDeleteIcon(_.iconId)
                                                }
                                            />
                                        </div>
                                    ))}
                                </UIContainer>
                            </>
                        )}

                        {!loading && useCustomView && (
                            <>
                                <Label
                                    value="Custom module name"
                                    description="What this module will be called"
                                />
                                <TextField
                                    value={customName || ""}
                                    fullWidth={true}
                                    onChange={event =>
                                        onChangeCustomName(event.target.value)
                                    }
                                    variant="filled"
                                    placeholder="My custom name"
                                />
                                {this.moduleDefinitionId === undefined && (
                                    <>
                                        <Label
                                            value="Streams"
                                            description="This lists the streams that this module will load data from."
                                        />
                                        <MultiSelect
                                            value={this.streams}
                                            title={
                                                streamName.startsWith(
                                                    "custom-module",
                                                )
                                                    ? "Streams"
                                                    : "Additional Streams"
                                            }
                                            options={this.knownStreams.map(
                                                _ => ({
                                                    value: _,
                                                }),
                                            )}
                                            onChange={
                                                this.onChangeCustomModuleStreams
                                            }
                                        />
                                    </>
                                )}
                                {!this.isDefinedCustomView && (
                                    <>
                                        <Label
                                            value="Custom module Url"
                                            description="This is the url of where the module will be loaded."
                                        />
                                        <TextField
                                            value={customVisualizationUrl || ""}
                                            fullWidth={true}
                                            onChange={event => {
                                                onChangeCustomView(
                                                    event.target.value,
                                                );
                                                onInvalidUrl(true);
                                                if (event.target.value) {
                                                    // here

                                                    const validUrlRegex =
                                                        /(?:^|\s)((https?:\/\/)?(?:localhost|[\w-]+(?:\.[\w-]+)+)(:\d+)?(\/\S*)?)/i;
                                                    try {
                                                        const parsedUrl =
                                                            new URL(
                                                                event.target
                                                                    .value,
                                                            );

                                                        if (
                                                            !parsedUrl.protocol.startsWith(
                                                                "http",
                                                            )
                                                        ) {
                                                            throw Error(
                                                                "unsupported protocol",
                                                            );
                                                        }

                                                        if (
                                                            !validUrlRegex.test(
                                                                event.target
                                                                    .value,
                                                            )
                                                        ) {
                                                            throw Error(
                                                                "invalid URL",
                                                            );
                                                        }
                                                    } catch {
                                                        onInvalidUrl(false);
                                                    }
                                                }
                                            }}
                                            variant="filled"
                                            placeholder="http://localhost:8000/chart.html"
                                            {...(!this.isValidUrl && {
                                                error: true,
                                                helperText:
                                                    "please use a valid URL",
                                            })}
                                        />
                                        <Label
                                            value="Enable Debug"
                                            description="Custom visualizations will show debug information in console and set {debug} to true if its in your url"
                                        />
                                        <ListButton>
                                            <Switch
                                                state={debug || false}
                                                onChange={this.onToggleDebug}
                                                label={debug ? "True" : "False"}
                                            />
                                        </ListButton>
                                    </>
                                )}
                            </>
                        )}
                        {!loading && (
                            <>
                                {moduleType === "custom" &&
                                    (this.moduleConfigurationSchema ? (
                                        <ModuleConfigurationSchemaForm
                                            schema={
                                                this.moduleConfigurationSchema
                                            }
                                            initialParams={
                                                this.moduleConfigurationJSON
                                            }
                                            onConfigurationChange={
                                                this
                                                    .onSchemafiedConfigurationChange
                                            }
                                        />
                                    ) : (
                                        <>
                                            <Label
                                                value="Json Configuration"
                                                description="Specify a json schema that corresponds to custom settings for the module"
                                            />
                                            <TextareaField
                                                placeholder="Module configuration JSON ..."
                                                value={
                                                    this.moduleConfigurationJSON
                                                }
                                                onChange={
                                                    this
                                                        .onChangeModuleConfiguration
                                                }
                                            />
                                        </>
                                    ))}
                            </>
                        )}
                    </Content>
                </ScrollerComponent>
                {!loading && (
                    <Footer>
                        <ButtonBar>
                            <Button onClick={onCancel}>Cancel</Button>
                            <Button
                                disabled={
                                    !this.isValidUrl ||
                                    !authStore.hasAccess(
                                        "views",
                                        "administrator",
                                    )
                                }
                                onClick={onSave}
                            >
                                {observeStore.configuringModule && "Save"}
                                {observeStore.configuringFlyout && "Done"}
                            </Button>
                        </ButtonBar>
                    </Footer>
                )}
            </>
        );
    }

    private popup() {
        return (
            <Container>
                <Popup
                    visible={true}
                    windowClassName="window"
                    onClose={this.onCancel}
                >
                    {this.prerender()}
                </Popup>
            </Container>
        );
    }

    public render() {
        if (observeStore.configuringModule) {
            return this.popup();
        }
        if (observeStore.configuringFlyout) {
            return this.prerender();
        }
    }

    private onSchemafiedConfigurationChange = (config: string) => {
        this.moduleConfigurationJSON = config;
    };

    private onChangeModuleConfiguration = (
        e: React.ChangeEvent<HTMLTextAreaElement>,
    ) => {
        this.moduleConfigurationJSON = e.target.value;
    };

    private onChangeIconId = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.iconId = e.target.value;
    };

    private onChangeIconUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.iconUrl = e.target.value;
    };

    private onAddIcon = () => {
        const { iconId, iconUrl } = this;
        if (iconId && iconUrl) {
            this.geoJsonIcons = [
                ...(this.geoJsonIcons || []),
                {
                    iconId,
                    url: iconUrl,
                },
            ];
            if (this.geoJsonIcons?.length > 50) {
                this.geoJsonIcons = this.geoJsonIcons.slice(0, 50);
            }
        }
    };

    private onDeleteIcon = (iconId: string) => {
        this.geoJsonIcons = this.geoJsonIcons.filter(_ => _.iconId !== iconId);
    };

    private onChangeCustomModuleStreams = (value: string[]) => {
        this.streams = value;
    };

    private onChangeGeoJsonStreams = (value: string[]) => {
        this.geoJsonStreams = value.length > 5 ? value.slice(0, 5) : value;
    };

    onChangeCustomName = (value: string) => {
        this.customName = value;
    };

    onChangeCustomView = (value: string) => {
        this.customVisualizationUrl = value;
    };

    onToggleCustomView = () => {
        this.useCustomView = !this.useCustomView;
    };

    onToggleVisibility = () => {
        this.visible = !this.visible;
    };

    onToggleDebug = () => {
        this.debug = !this.debug;
    };

    onToggleShowOnlyCurrentValues = () => {
        this.showOnlyCurrentValues = !this.showOnlyCurrentValues;
    };

    onInvalidUrl = (value: boolean) => {
        this.isValidUrl = value;
    };

    onCancel() {
        observeStore.configuringModule = undefined;
        observeStore.configuringFlyout = undefined;
    }

    getConfiguration = async (): Promise<
        ILayoutModuleConfiguration<AddableModuleType>
    > => {
        if (!this.useCustomView) {
            this.customName = undefined;
            this.customVisualizationUrl = undefined;
            this.streams = [];
        }

        let newConfig: ILayoutModuleConfiguration;
        if (this.props.moduleType === "location") {
            newConfig = {
                moduleType: "location",
                customVisualizationUrl: this.customVisualizationUrl,
                customName: this.customName,
                debug: this.debug,
                showOnlyCurrentValues: this.showOnlyCurrentValues,
                streams: this.streams,
                parameters: {
                    geoJsonLayers: this.geoJsonStreams.map(_ => ({
                        streamId: _,
                    })),
                    geoJsonIcons: this.geoJsonIcons,
                },
                visible: this.visible,
            };
        } else {
            newConfig = {
                moduleType: "custom",
                customVisualizationUrl: this.customVisualizationUrl,
                customName: this.customName,
                debug: this.debug,
                showOnlyCurrentValues: this.showOnlyCurrentValues,
                streams: this.streams,
                visible: this.visible,
                parameters: {
                    customModuleDefinitionId: this.moduleDefinitionId,
                },
            };
        }

        if (this.props.moduleType === "custom") {
            let data = this.moduleConfigurationJSON;
            if (!data && this.moduleConfigurationSchema) {
                data = JSON.stringify({}); // initializes an empty configuration object if the schema is present
            }

            if (data) {
                const currentId = this.moduleConfigurationId;

                let id: string;
                if (!currentId) {
                    const c = await customModuleStore.createConfiguration({
                        configuration: data,
                    });
                    id = c.id;
                } else {
                    await customModuleStore.updateConfiguration(currentId, {
                        configuration: data,
                    });
                    id = currentId;
                }

                newConfig.parameters = {
                    customModuleConfigurationId: id,
                    customModuleDefinitionId: this.moduleDefinitionId,
                } as ICustomModuleParameters;
            }
        }
        return newConfig;
    };

    onSave = async () => {
        const newConfig = await this.getConfiguration();
        const { streamName } = this.props;
        const { configuringModule, configuringFlyout } = observeStore;

        if (configuringModule) {
            defined(configuringModule)[1][streamName].configuration = newConfig;
            defined(configuringModule)[2](defined(configuringModule)[1]);
            observeStore.configuringModule = undefined;
        }
        if (configuringFlyout) {
            defined(configuringFlyout)[0][streamName].configuration = newConfig;
            defined(configuringFlyout)[2](defined(configuringFlyout)[0]);
            observeStore.configuringFlyout = undefined;
        }
    };

    handleDefaultModulesConfiguation = async () => {
        const configurationIndex = customModuleList
            .map(_ => _.name)
            .indexOf(defined(this.customName));
        if (configurationIndex < 0) {
            return;
        }
        const { configurationSchemaUrl } = customModuleList[configurationIndex];
        if (
            configurationSchemaUrl !== undefined &&
            configurationSchemaUrl !== null
        ) {
            await this.fetchModuleConfiguration(configurationSchemaUrl);
        }
    };

    fetchModuleConfiguration = async (url: string) => {
        try {
            const actualUrl = url;
            const fileResponse = await fetch(actualUrl);
            const fileText = await fileResponse.text();
            this.moduleConfigurationSchema = JSON.parse(fileText);
        } catch (e) {
            appStore.setSnackbar(
                "Failed to load configuration schema for module",
            );
        }
    };
}

const Container = styled.div`
    .window {
        width: 22.5rem;
        min-width: 22.5rem;
        min-height: 33.8125rem;
        height: 33.8125rem;
        padding: 0rem;
        margin: 0rem;
    }

    input {
        width: 18rem;
        max-height: 1rem;
        padding-right: 0.625rem;
        margin-top: -0.8rem;
    }
`;

export const Content = styled.div`
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    width: 100%;
`;

const Footer = styled.div`
    margin-top: 1.2rem;
    margin-left: 1.5rem;
    margin-right: 1.5rem;
`;
